import { Controller } from "@hotwired/stimulus"

/**
 * Cascading Filter Controller
 *
 * Manages dependent cascading dropdowns for filter fields.
 * Each level's options are filtered based on the parent level's selection.
 *
 * Example: Survey → Question → Option
 * - Selecting a Survey enables the Question dropdown and filters to that survey's questions
 * - Selecting a Question enables the Option dropdown and filters to that question's options
 *
 * Usage:
 * <div data-controller="cascading-filter"
 *      data-cascading-filter-chain-value='[{key: "survey_lookup", label: "Survey", lookup_url: "..."}, ...]'
 *      data-cascading-filter-values-value='{"survey_lookup": 123, "question_lookup": 456}'>
 */
export default class extends Controller {
  static targets = ["level"]
  static values = {
    chain: Array,    // [{key, label, lookup_url, depends_on, extra_attributes}, ...]
    values: Object   // {survey_lookup: 123, survey_lookup_label: "...", question_lookup: 456, ...}
  }

  connect() {
    // Initialize level states on connect
    this.updateLevelStates()
  }

  /**
   * Called when a level's tom-select value changes
   * Triggered by: data-action="tom-select:change->cascading-filter#levelChanged"
   */
  levelChanged(event) {
    const wrapper = event.currentTarget
    const levelIndex = parseInt(wrapper.dataset.level)
    const selectedValue = event.detail?.value
    const tomSelect = event.detail?.tomSelect
    const levelConfig = this.chainValue[levelIndex]

    if (!levelConfig) return

    // Get label from tomSelect for display purposes
    let selectedLabel = null
    if (tomSelect && selectedValue) {
      const option = tomSelect.options[selectedValue]
      if (option) {
        selectedLabel = option.text || option.label || selectedValue
      }
    }

    // Update stored values
    const newValues = { ...this.valuesValue }
    newValues[levelConfig.key] = selectedValue
    newValues[`${levelConfig.key}_label`] = selectedLabel

    // Extract extra attributes if configured
    if (tomSelect && selectedValue && levelConfig.extra_attributes) {
      const option = tomSelect.options[selectedValue]
      if (option) {
        levelConfig.extra_attributes.forEach(attr => {
          newValues[`${levelConfig.key}_${attr}`] = option[attr]
        })
      }
    }

    // Clear all child level values (they're no longer valid)
    this.chainValue.forEach((config, index) => {
      if (index > levelIndex) {
        delete newValues[config.key]
        delete newValues[`${config.key}_label`]
        if (config.extra_attributes) {
          config.extra_attributes.forEach(attr => {
            delete newValues[`${config.key}_${attr}`]
          })
        }
      }
    })

    this.valuesValue = newValues

    // Clear and disable child level tom-selects
    this.clearChildLevels(levelIndex)

    // Enable next level if value selected
    if (selectedValue) {
      this.enableNextLevel(levelIndex, selectedValue)
    }

    // Dispatch change event for parent builder controller
    this.dispatch("cascadeChanged", {
      detail: {
        values: this.valuesValue,
        index: this.element.dataset.filterIndex || this.element.dataset.groupIndex,
        nestedIndex: this.element.dataset.nestedFilterIndex
      }
    })
  }

  /**
   * Clear all tom-selects below the given parent level
   */
  clearChildLevels(parentIndex) {
    this.levelTargets.forEach((levelElement, index) => {
      if (index > parentIndex) {
        const tomSelectWrapper = levelElement.querySelector('[data-controller="tom-select"]')
        const select = levelElement.querySelector('select')

        if (tomSelectWrapper && select?.tomselect) {
          const ts = select.tomselect
          ts.clear()
          ts.clearOptions()
          ts.disable()
        } else if (select) {
          select.disabled = true
          select.innerHTML = ''
        }
      }
    })
  }

  /**
   * Enable the next level dropdown with parent_value in the URL
   */
  enableNextLevel(parentIndex, parentValue) {
    const nextIndex = parentIndex + 1
    if (nextIndex >= this.chainValue.length) return

    const nextLevelElement = this.levelTargets[nextIndex]
    const nextConfig = this.chainValue[nextIndex]
    if (!nextLevelElement || !nextConfig) return

    const tomSelectWrapper = nextLevelElement.querySelector('[data-controller="tom-select"]')
    const select = nextLevelElement.querySelector('select')

    if (tomSelectWrapper && select?.tomselect) {
      const ts = select.tomselect

      // Update the remote URL with parent_value
      const baseUrl = new URL(nextConfig.lookup_url, window.location.origin)
      baseUrl.searchParams.set('parent_value', parentValue)

      // Update the tom-select config to use new URL
      ts.settings.load = (query, callback) => {
        const url = new URL(baseUrl)
        url.searchParams.set('q', query)

        fetch(url.toString())
          .then(response => response.json())
          .then(data => callback(data))
          .catch(() => callback())
      }

      ts.clearOptions()
      ts.enable()
      ts.load('') // Load initial options
    } else if (select) {
      select.disabled = false
    }
  }

  /**
   * Update level enabled/disabled states based on current values
   * Called on connect to restore state from existing values
   */
  updateLevelStates() {
    this.chainValue.forEach((config, index) => {
      if (index === 0) {
        // First level is always enabled
        this.enableLevel(index)
        return
      }

      const parentConfig = this.chainValue[index - 1]
      const parentValue = this.valuesValue[parentConfig.key]

      if (parentValue) {
        // Parent has value, enable this level with parent filter
        this.enableNextLevel(index - 1, parentValue)
      } else {
        // Parent has no value, disable this level
        this.disableLevel(index)
      }
    })
  }

  /**
   * Enable a specific level
   */
  enableLevel(levelIndex) {
    const levelElement = this.levelTargets[levelIndex]
    if (!levelElement) return

    const select = levelElement.querySelector('select')
    if (select?.tomselect) {
      select.tomselect.enable()
    } else if (select) {
      select.disabled = false
    }
  }

  /**
   * Disable a specific level
   */
  disableLevel(levelIndex) {
    const levelElement = this.levelTargets[levelIndex]
    if (!levelElement) return

    const select = levelElement.querySelector('select')
    if (select?.tomselect) {
      select.tomselect.disable()
    } else if (select) {
      select.disabled = true
    }
  }

  /**
   * Get the final selected values for filter application
   * This extracts the values needed by the filter applier
   */
  getFinalValues() {
    return this.valuesValue
  }
}
